import { Button, Group, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconMinus,
  IconPlus,
  IconRefresh,
  IconShoppingCart,
  IconShoppingCartPlus,
  IconShoppingCartX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { MantineNotificationPosition } from "common/constants";
import { findItemByCode } from "common/utils";
import { useCartStore } from "stores/useCartStore";
import { useSessionStore } from "stores/userSessionStore";
import { COLORS } from "styles/colors";
import { ProductSummary } from "types/model.types";
import { getProductTotalPrice } from "utils/helper";

interface AddToCartControlsProps {
  product: ProductSummary;
  size?: "xs" | "sm" | "md" | "lg" | "xl"; // Optional and type-safe
}

export const AddToCartControls = ({
  product,
  size,
}: AddToCartControlsProps) => {
  const existingQty = useCartStore(
    (s) =>
      findItemByCode(s.cart?.items ?? [], product.productCode)?.quantity ?? 0,
  );
  const { priceLevel } = useSessionStore();
  const addItem = useCartStore((s) => s.addItem);
  const removeItem = useCartStore((s) => s.removeItem);
  const [quantity, setQuantity] = useState(existingQty || 1);
  const [clicked, setClicked] = useState(false);

  const notify = (action: "add" | "update" | "remove", count: number) => {
    const iconMap = {
      add: <IconShoppingCartPlus size={16} />,
      update: <IconShoppingCart size={16} />,
      remove: <IconShoppingCartX size={16} />,
    };
    const messageMap = {
      add: `${count} x ${name} added to cart`,
      update: `Quantity for ${name} updated to ${count}`,
      remove: `${name} removed from cart`,
    };

    notifications.show({
      title: "Cart Updated",
      message: messageMap[action],
      color: action === "remove" ? "gray" : "teal",
      position: MantineNotificationPosition.TopCenter,
      icon: iconMap[action],
    });
  };

  const handleUpdate = () => {
    if (!product.id) return;
    setClicked(true);
    setTimeout(() => setClicked(false), 400);

    if (quantity === 0) {
      removeItem(product.id);
      notify("remove", 0);
    } else {
      const isNew = existingQty === 0;
      addItem({
        id: "",
        productCode: product.productCode,
        title: product.title,
        price: getProductTotalPrice(product, priceLevel),
        unitsCtn: product.unitsCtn ?? "",
        quantity: quantity,
        productId: product.id.toString(), // if available
        featuredImageName: product.featuredImageName,
      });

      notify(isNew ? "add" : "update", quantity);
    }
  };

  useEffect(() => {
    if (existingQty > 0 && quantity !== existingQty) {
      handleUpdate();
    }
  }, [quantity]);

  return (
    <Group gap={4} justify="center" mt="xs">
      <Button
        variant="light"
        size={size ?? "sm"}
        color="gray"
        style={{ padding: "0 10px" }}
        onClick={() => setQuantity((q) => Math.max(0, q - 1))}
        disabled={quantity <= 0}
      >
        <IconMinus size={14} />
      </Button>

      <TextInput
        type="number"
        size={size ?? "sm"}
        readOnly
        value={quantity}
        style={{
          width: 48,
          textAlign: "center",
          padding: "0 4px",
          fontSize: 12,
        }}
        min={0}
        max={100}
      />

      <Button
        variant="light"
        size={size ?? "sm"}
        color="gray"
        style={{ padding: "0 10px" }}
        onClick={() => setQuantity((q) => Math.min(100, q + 1))}
        disabled={quantity >= 100}
      >
        <IconPlus size={14} />
      </Button>

      <Button
        size={size ?? "sm"}
        color={COLORS.colorRed}
        onClick={handleUpdate}
        disabled={quantity < 1}
        style={{
          transition: "transform 0.2s ease",
          transform: clicked ? "scale(1.06)" : "scale(1)",
        }}
      >
        {existingQty > 0 ? (
          <Group>
            <IconRefresh size={14} />
            {size && <Text>Update Cart</Text>}
          </Group>
        ) : (
          <Group>
            <IconShoppingCartPlus size={14} />
            {size && <Text>Add to Cart</Text>}
          </Group>
        )}
      </Button>
    </Group>
  );
};
